import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Definição dos tipos de estabelecimento (espelha constants.py do backend)
export type EstabelecimentoTipo =
  // Beleza
  | 'BARBEARIA'
  | 'SALAO_BELLEZA'
  | 'ESTETICA'
  | 'TATUAGEM'
  | 'MANICURE'
  | 'MAQUIAGEM'
  // Saúde
  | 'CLINICA'
  | 'CONSULTORIO'
  | 'CLINICA_MEDICA'
  | 'CLINICA_ODONTO'
  | 'CLINICA_PSICO'
  | 'CLINICA_FISIO'
  | 'CLINICA_NUTRI'
  | 'CLINICA_ESTETIC'
  | 'CLINICA_OFTALMO'
  | 'CLINICA_ORTO'
  // Fitness
  | 'ACADEMIA'
  | 'ESTUDIO_PILATES'
  | 'PERSONAL'
  | 'CROSSFIT'
  | 'YOGA'
  | 'DANCA'
  // Jurídico/Profissional
  | 'ADVOCACIA'
  | 'CONTABILIDADE'
  | 'CONSULTORIA'
  | 'ARQUITETURA'
  | 'ENGENHARIA'
  // Educação
  | 'ESCOLA_IDIOMAS'
  | 'ESCOLA_MUSICA'
  | 'COACHING'
  | 'TUTORIA'
  // Genérico
  | 'OUTROS';

// Mapeamento de tipo para tema
const tipoParaTema: Record<EstabelecimentoTipo, string> = {
  // Beleza → temas próprios
  'BARBEARIA': 'barbearia',
  'SALAO_BELLEZA': 'salao-belleza',
  'ESTETICA': 'salao-belleza',
  'TATUAGEM': 'tatuagem',
  'MANICURE': 'salao-belleza',
  'MAQUIAGEM': 'salao-belleza',
  // Saúde → tema clínica
  'CLINICA': 'clinica',
  'CONSULTORIO': 'clinica',
  'CLINICA_MEDICA': 'clinica',
  'CLINICA_ODONTO': 'clinica',
  'CLINICA_PSICO': 'clinica',
  'CLINICA_FISIO': 'clinica',
  'CLINICA_NUTRI': 'clinica',
  'CLINICA_ESTETIC': 'clinica',
  'CLINICA_OFTALMO': 'clinica',
  'CLINICA_ORTO': 'clinica',
  // Fitness → tema fitness
  'ACADEMIA': 'fitness',
  'ESTUDIO_PILATES': 'fitness',
  'PERSONAL': 'fitness',
  'CROSSFIT': 'fitness',
  'YOGA': 'fitness',
  'DANCA': 'fitness',
  // Jurídico/Profissional → tema corporativo
  'ADVOCACIA': 'corporativo',
  'CONTABILIDADE': 'corporativo',
  'CONSULTORIA': 'corporativo',
  'ARQUITETURA': 'corporativo',
  'ENGENHARIA': 'corporativo',
  // Educação → tema educacao
  'ESCOLA_IDIOMAS': 'educacao',
  'ESCOLA_MUSICA': 'educacao',
  'COACHING': 'educacao',
  'TUTORIA': 'educacao',
  // Genérico
  'OUTROS': 'default',
};

interface ThemeContextType {
  currentTheme: string;
  setTheme: (theme: string) => void;
  applyThemeByTipo: (tipo: EstabelecimentoTipo) => void;
  availableThemes: Array<{
    id: string;
    name: string;
    description: string;
    tipo: EstabelecimentoTipo[];
  }>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<string>('default');
  const { user } = useAuth();

  // Lista de temas disponíveis
  const availableThemes = useMemo(() => [
    {
      id: 'default',
      name: 'Padrão',
      description: 'Tema escuro elegante com dourado',
      tipo: ['OUTROS' as EstabelecimentoTipo]
    },
    {
      id: 'barbearia',
      name: 'Barbearia',
      description: 'Clássico masculino com cores sóbrias',
      tipo: ['BARBEARIA' as EstabelecimentoTipo]
    },
    {
      id: 'salao-belleza',
      name: 'Salão de Beleza',
      description: 'Elegante e moderno com lilás e dourado',
      tipo: [
        'SALAO_BELLEZA' as EstabelecimentoTipo,
        'ESTETICA' as EstabelecimentoTipo,
        'MANICURE' as EstabelecimentoTipo,
        'MAQUIAGEM' as EstabelecimentoTipo,
      ]
    },
    {
      id: 'clinica',
      name: 'Clínica / Saúde',
      description: 'Profissional com azul e verde tecnológico',
      tipo: [
        'CLINICA' as EstabelecimentoTipo,
        'CONSULTORIO' as EstabelecimentoTipo,
        'CLINICA_MEDICA' as EstabelecimentoTipo,
        'CLINICA_ODONTO' as EstabelecimentoTipo,
        'CLINICA_PSICO' as EstabelecimentoTipo,
        'CLINICA_FISIO' as EstabelecimentoTipo,
        'CLINICA_NUTRI' as EstabelecimentoTipo,
        'CLINICA_ESTETIC' as EstabelecimentoTipo,
        'CLINICA_OFTALMO' as EstabelecimentoTipo,
        'CLINICA_ORTO' as EstabelecimentoTipo,
      ]
    },
    {
      id: 'tatuagem',
      name: 'Estúdio de Tatuagem',
      description: 'Urbano e vibrante com cores fortes',
      tipo: ['TATUAGEM' as EstabelecimentoTipo]
    },
    {
      id: 'fitness',
      name: 'Fitness / Academia',
      description: 'Energético com laranja e verde neon',
      tipo: [
        'ACADEMIA' as EstabelecimentoTipo,
        'ESTUDIO_PILATES' as EstabelecimentoTipo,
        'PERSONAL' as EstabelecimentoTipo,
        'CROSSFIT' as EstabelecimentoTipo,
        'YOGA' as EstabelecimentoTipo,
        'DANCA' as EstabelecimentoTipo,
      ]
    },
    {
      id: 'corporativo',
      name: 'Corporativo',
      description: 'Sóbrio e confiável com navy e dourado',
      tipo: [
        'ADVOCACIA' as EstabelecimentoTipo,
        'CONTABILIDADE' as EstabelecimentoTipo,
        'CONSULTORIA' as EstabelecimentoTipo,
        'ARQUITETURA' as EstabelecimentoTipo,
        'ENGENHARIA' as EstabelecimentoTipo,
      ]
    },
    {
      id: 'educacao',
      name: 'Educação',
      description: 'Inspirador com azul e amarelo',
      tipo: [
        'ESCOLA_IDIOMAS' as EstabelecimentoTipo,
        'ESCOLA_MUSICA' as EstabelecimentoTipo,
        'COACHING' as EstabelecimentoTipo,
        'TUTORIA' as EstabelecimentoTipo,
      ]
    },
  ], []);

  // Aplicar tema ao body
  const applyTheme = useCallback((themeName: string) => {
    const body = document.body;

    // Remover todas as classes de tema
    availableThemes.forEach(theme => {
      body.classList.remove(`theme-${theme.id}`);
    });

    // Adicionar nova classe de tema
    if (themeName !== 'default') {
      body.classList.add(`theme-${themeName}`);
    }

    setCurrentTheme(themeName);

    // Salvar preferência no localStorage
    localStorage.setItem('selectedTheme', themeName);
  }, [availableThemes]);

  // Aplicar tema baseado no tipo de estabelecimento
  const applyThemeByTipo = useCallback((tipo: EstabelecimentoTipo) => {
    const themeName = tipoParaTema[tipo];
    applyTheme(themeName);
  }, [applyTheme]);

  // Definir tema manualmente
  const setTheme = useCallback((themeName: string) => {
    applyTheme(themeName);
  }, [applyTheme]);

  // Carregar tema salvo ou aplicar tema baseado no estabelecimento do usuário
  useEffect(() => {
    const savedTheme = localStorage.getItem('selectedTheme');

    if (savedTheme && availableThemes.find(t => t.id === savedTheme)) {
      applyTheme(savedTheme);
    } else if (user?.estabelecimento?.tipo) {
      applyThemeByTipo(user.estabelecimento.tipo as EstabelecimentoTipo);
    } else {
      applyTheme('default');
    }
  }, [user, applyTheme, applyThemeByTipo, availableThemes]);

  // Importar CSS dos temas diretamente
  useEffect(() => {
    // Desabilitar carregamento dinâmico de temas para evitar erros 404
    // Os temas já estão incluídos no bundle principal via CSS imports
    // console.log('Theme system initialized - using bundled CSS'); // Removido em produção
  }, []);

  const value: ThemeContextType = {
    currentTheme,
    setTheme,
    applyThemeByTipo,
    availableThemes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};